/**
 * Queues manifest-backed art from any scene.
 *
 * Preload loads what the menus need; Game loads the animal and environment
 * that this particular level actually uses. Loading everything up front cost
 * 1.7s and 426MB of texture memory, almost all of it art the player would
 * never see on this run.
 */
import type Phaser from 'phaser';
import { ART_KEYS_REGISTRY, artResolution, resolutionFor } from './artScale';
import { manifestFileFor, manifestSizeFor } from './placeholders';
import type { AssetKey, AssetManifest } from '../types';

/** Where Preload parks the manifest so other scenes can load from it too. */
export const MANIFEST_REGISTRY = 'assetManifest';

function loadedMap(scene: Phaser.Scene): Map<AssetKey, number> {
  const existing: unknown = scene.registry.get(ART_KEYS_REGISTRY);
  if (existing instanceof Map) return existing as Map<AssetKey, number>;
  const fresh = new Map<AssetKey, number>();
  scene.registry.set(ART_KEYS_REGISTRY, fresh);
  return fresh;
}

/**
 * Queue every key the manifest can satisfy that is not already loaded.
 *
 * Returns how many were queued. Keys the manifest does not know are skipped
 * silently: the caller's placeholder covers them.
 */
export function queueArt(scene: Phaser.Scene, keys: Iterable<AssetKey>): number {
  const manifest: unknown = scene.registry.get(MANIFEST_REGISTRY);
  if (manifest === null || typeof manifest !== 'object') return 0;

  const loaded = loadedMap(scene);
  const full = artResolution(scene);
  let queued = 0;
  for (const key of keys) {
    if (loaded.has(key) || scene.textures.exists(key)) continue;
    const file = manifestFileFor(manifest as AssetManifest, key);
    if (file === null) continue;

    const size = manifestSizeFor(manifest as AssetManifest, key);
    if (size === null) {
      scene.load.svg(key, file);
      loaded.set(key, 1);
    } else {
      const resolution = resolutionFor(key, full);
      scene.load.svg(key, file, {
        width: Math.round(size.width * resolution),
        height: Math.round(size.height * resolution),
      });
      loaded.set(key, resolution);
    }
    queued += 1;
  }
  return queued;
}

/**
 * Drop a key's recorded resolution, after its file failed to load.
 *
 * The resolution is recorded when the file is queued, not when it arrives, so
 * a failed load leaves a divisor behind that no longer matches the 1x
 * placeholder standing in for it.
 */
export function forgetArtKey(scene: Phaser.Scene, key: AssetKey): void {
  const loaded: unknown = scene.registry.get(ART_KEYS_REGISTRY);
  if (loaded instanceof Map) loaded.delete(key);
}
